import { ApolloError, ApolloLink, ServerError, ServerParseError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { isBrowserOffline } from '@bloomays-lib/utils.shared/browser';
import { monitorException, withScope } from '../monitoring';
import { Scope } from '@sentry/react';
import { GraphQLError, GraphQLFormattedError } from 'graphql';

type MonitoringOptions = {
  filterExceptions: (error: GraphQLError | Error | ServerParseError | ServerError | GraphQLFormattedError) => boolean;
};

const filterNothing = (g: any) => {
  return g;
};

/**
 * Reaches into the various shapes Apollo can hand us for a network error and
 * returns the HTTP status if any (`ServerError`, `Response`, or a plain object
 * with `statusCode`). Missing status means we treat the error as a transport
 * failure rather than a server response.
 */
const extractNetworkStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const err = error as {
    statusCode?: number;
    status?: number;
    response?: { status?: number };
  };
  return err.statusCode ?? err.status ?? err.response?.status;
};

/**
 * Apollo error link that forwards non-filtered errors to Sentry / Datadog RUM
 * with extra context (operation name, variables, HTTP status, online state).
 *
 * A `TypeError: Failed to fetch` raised while `navigator.onLine === false` is
 * almost always caused by the user losing connectivity. Reporting those to
 * Sentry only pollutes the issue list (cf. TEC-1684 / Sentry FORM-BLOOMERS-J1),
 * so we skip them and rely on the client-side retry link / UI to react.
 */
export const errorLinkMonitoring = (options?: MonitoringOptions): ApolloLink => {
  return onError(({ graphQLErrors, networkError, operation }) => {
    withScope((scope: Scope) => {
      const isOffline = isBrowserOffline();
      const networkStatus = networkError ? extractNetworkStatusCode(networkError) : undefined;

      scope.setContext('Apollo', {
        operationName: operation.operationName,
        variables: operation.variables,
        query: JSON.stringify(operation.query, null, 4),
        isOffline,
        networkStatus,
        networkErrorName: networkError?.name,
        networkErrorMessage: networkError?.message,
      });
      scope.setTag('apollo.offline', String(isOffline));
      if (networkStatus !== undefined) {
        scope.setTag('apollo.http_status', String(networkStatus));
      }

      const filterExceptions = options?.filterExceptions || filterNothing;
      if (graphQLErrors) {
        if (!filterExceptions(graphQLErrors[0])) {
          const err = new ApolloError({ graphQLErrors });
          scope.setFingerprint([err.message]);
          monitorException(err, {
            extra: {
              graphQLErrors: graphQLErrors,
            },
          });
        }
      }
      if (networkError) {
        if (isOffline) {
          return;
        }
        if (!filterExceptions(networkError)) {
          const err = new ApolloError({ networkError });
          scope.setFingerprint([err.message]);
          monitorException(err, {
            extra: {
              networkError: networkError,
              networkStatus,
            },
          });
        }
      }
    });
  });
};
